// Dev tool for generating and saving game cover images.

import React, { useState } from 'react';
import { gameLibrary } from '@airhead-games/shared/configs';
import { ImagePlusIcon, SaveIcon } from 'lucide-react';
import { useAuth } from '../hooks';
import {
  generateCoverImage,
  saveCoverImage,
} from '../transport/api/protectedApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { InputGroup, InputGroupTextarea } from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/utilities/cn';

interface Candidate {
  index: number;
  base64: string;
  mimeType: string;
  sizeKb: number;
}

const GameCoverChooser: React.FC = () => {
  const { user } = useAuth();
  const [selectedGameId, setSelectedGameId] = useState('');
  const [notes, setNotes] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFileName, setSavedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedGame = gameLibrary.find(
    (g) => g.gameCore.gameId === selectedGameId
  );

  const handleGenerate = async () => {
    if (!selectedGameId || !user) return;

    setGenerating(true);
    setError(null);
    setCandidates([]);
    setRequestId(null);
    setSelectedIndex(null);
    setSavedFileName(null);

    try {
      const result = await generateCoverImage({
        gameId: selectedGameId,
        notes: notes || undefined,
        userPreferences: user.userPreferences,
      });
      setRequestId(result.data.requestId);
      setCandidates(result.data.candidates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (selectedIndex === null || !selectedGame || !requestId) return;

    setSaving(true);
    setError(null);

    try {
      const gameName = selectedGame.gameCore.gameName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const result = await saveCoverImage({
        requestId,
        index: selectedIndex,
        gameName,
      });
      setSavedFileName(result.data.fileName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-svh overflow-auto bg-background p-4 text-foreground sm:p-6">
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Game Cover Image Generator</CardTitle>
            <CardDescription>
              Generate cover candidates and save the selected image to the CDN.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FieldGroup>
              <Field>
                <FieldLabel>Game</FieldLabel>
                <Select
                  value={selectedGameId}
                  onValueChange={setSelectedGameId}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select a game..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {gameLibrary.map((g) => (
                        <SelectItem
                          key={g.gameCore.gameId}
                          value={g.gameCore.gameId}
                        >
                          {g.gameCore.gameName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Notes</FieldLabel>
                <InputGroup className="max-w-2xl">
                  <InputGroupTextarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional instructions for image generation..."
                    rows={3}
                  />
                </InputGroup>
                <FieldDescription>
                  Optional generation guidance.
                </FieldDescription>
              </Field>
            </FieldGroup>

            <div>
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={!selectedGameId || generating}
              >
                {generating ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <ImagePlusIcon data-icon="inline-start" />
                )}
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTitle>Request failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {savedFileName && (
              <Alert>
                <AlertTitle>Saved</AlertTitle>
                <AlertDescription>
                  <code>{savedFileName}</code>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {candidates.length > 0 && (
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading text-2xl leading-tight font-semibold tracking-tight">
                Candidates
              </h2>
              {selectedIndex !== null && (
                <Button type="button" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Spinner data-icon="inline-start" />
                  ) : (
                    <SaveIcon data-icon="inline-start" />
                  )}
                  {saving ? 'Saving...' : 'Save to CDN'}
                </Button>
              )}
            </div>

            <div className="grid items-start gap-4 lg:grid-cols-2">
              {candidates.map((candidate) => {
                const selected = selectedIndex === candidate.index;

                return (
                  <Card
                    key={candidate.index}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    data-selected={selected ? 'true' : undefined}
                    onClick={() => setSelectedIndex(candidate.index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedIndex(candidate.index);
                      }
                    }}
                    className={cn(
                      'gap-0 overflow-hidden p-0 py-0 outline-none transition-[box-shadow,transform]',
                      'hover:shadow-md focus-visible:ring-3 focus-visible:ring-ring/50',
                      'data-[selected=true]:ring-2 data-[selected=true]:ring-primary'
                    )}
                  >
                    <img
                      src={`data:${candidate.mimeType};base64,${candidate.base64}`}
                      alt={`Candidate ${candidate.index + 1}`}
                      className="aspect-[4/3] w-full object-cover"
                    />
                    <CardFooter className="justify-between">
                      <span>Candidate {candidate.index + 1}</span>
                      <Badge variant={selected ? 'default' : 'secondary'}>
                        {candidate.sizeKb} KB
                      </Badge>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default GameCoverChooser;
